import { ApolloError } from '@apollo/client';
import { apolloClient } from 'graphql/apolloClient';
import {
  UPDATE_FIELD_INSTANCE
} from 'graphql/UserInstanceAPI/mutations';
import { FieldInstance } from 'types/interfaces';
import { BugTracker } from 'Utils/Bugtracker';
import { removeTypenames } from '../User Instance API/mutations';
import { GetCompleteUserInstanceDetail } from '../User Instance API';
import { queryClient } from 'queryClient';
import { cosneticsQueryKeys } from '../cosneticsQueryKeys';
import { normalizeFieldInstance } from '../User Instance API/normalizers';

/**
 * Updates a FieldInstance using the provided parameters.
 * @param {string} baseUrl - The base URL for the hostname.
 * @param {number} ProcessInstanceId - The identifier of the ProcessInstanceId to update.
 * @param {FieldInstance} data - The data object containing the updated FieldInstance information.
 * @returns {Promise<FieldInstance | ApolloError>} - A promise resolving to the updated FieldInstance or an ApolloError.
 */
export const UpdateField = async ({
  baseUrl,
  ProcessInstanceId,
  data: payload
}: {
  baseUrl: string;
  ProcessInstanceId: number;
  data: FieldInstance;
}): Promise<FieldInstance | ApolloError> => {
  try {
    const finalData = normalizeFieldInstance({
      ...removeTypenames(payload),
      IsPublished: true
    }) as FieldInstance;

    const { data, errors } = await apolloClient.mutate({
      mutation: UPDATE_FIELD_INSTANCE,
      variables: {
        baseUrl,
        processInstanceId: ProcessInstanceId,
        data: finalData
      }
    });

    if (errors && errors.length > 0) {
      return new ApolloError({
        errorMessage: errors.map((err) => err.message).join(', ')
      });
    }

    const userInstanceId = Number(finalData.UserInstanceId);
    if (userInstanceId) {
      await GetCompleteUserInstanceDetail({
        baseUrl,
        fetchPolicy: true,
        UserInstanceId: userInstanceId
      });
    }
    await queryClient.invalidateQueries(
      cosneticsQueryKeys.usersForProcessAll
    );

    return data.UpdateField as FieldInstance;
  } catch (e) {
    BugTracker.notify(e);

    const error = e as Error;
    const errorMessage = error.message || 'An unexpected error occurred';
    return new ApolloError({ errorMessage });
  }
};
